import { useEffect, useState } from "react";
import { api } from "@/client/lib/api";
import { Button } from "@/client/components/ui/button";
import { Input } from "@/client/components/ui/input";
import { useAdminSaveToast } from "@/client/components/admin/AdminSaveToast";
import { CalendarClockIcon } from "lucide-react";

// Persisted, app-wide planning deadline. A test control: set it (e.g. to yesterday)
// to see the "na de deadline" warnings on both the KUVO and school side. Deliberately
// minimal — it's just one stored date, nothing else is built on top of it.
//
// Shared between the dashboard and /admin/configuratie; both read and write the same
// setting via the dashboard RPC (settings/planning-deadline), so wherever KUVO sets it
// the value is the same single app-wide deadline.
export function PlanningDeadlineControl() {
  const [deadline, setDeadline] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const savedToast = useAdminSaveToast();

  useEffect(() => {
    (async () => {
      try {
        const res = await (api.api.admin.dashboard as any).settings["planning-deadline"].$get();
        if (res.ok) {
          const d = await res.json();
          setDeadline(d.deadline ?? null);
          setDraft(d.deadline ? new Date(d.deadline).toISOString().slice(0, 10) : "");
        }
      } catch { /* ignore */ }
      setLoaded(true);
    })();
  }, []);

  async function save(value: string | null) {
    setSaving(true);
    try {
      const res = await (api.api.admin.dashboard as any).settings["planning-deadline"].$put({ json: { deadline: value } });
      if (res.ok) {
        const d = await res.json();
        setDeadline(d.deadline ?? null);
        savedToast.show(value === null ? "Deadline gewist" : "Opgeslagen");
      }
    } catch { /* ignore */ }
    setSaving(false);
  }

  const handleSave = () => {
    if (!draft) { save(null); return; }
    // End of the selected day (local) so a deadline counts same-day submissions on time.
    save(new Date(`${draft}T23:59:59.999`).toISOString());
  };

  const started = deadline ? new Date(deadline).getTime() < Date.now() : false;
  const niceDate = deadline ? new Date(deadline).toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" }) : "";

  if (!loaded) return null;

  return (
    <div className="rounded-lg border bg-muted/20 p-3">
      <h2 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
        <CalendarClockIcon size={15} /> Planningsdeadline
        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">test</span>
      </h2>
      <p className="mt-1 max-w-prose text-xs text-muted-foreground">
        {deadline
          ? <>Deadline: <span className="font-medium text-foreground">{niceDate}</span>. {started ? "Aanleveringen worden bij KUVO en de school als ‘na de deadline’ gemarkeerd." : "Aanleveringen na deze datum worden gemarkeerd."}</>
          : "Geen deadline ingesteld. Stel er een in om de waarschuwingen voor late aanleveringen te testen."}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Input type="date" value={draft} onChange={(e) => setDraft(e.target.value)} className="w-44" />
        <Button size="sm" onClick={handleSave} disabled={saving}>{saving ? "Opslaan..." : "Opslaan"}</Button>
        {deadline && <Button size="sm" variant="ghost" onClick={() => { setDraft(""); save(null); }} disabled={saving}>Wissen</Button>}
      </div>
      {savedToast.toast}
    </div>
  );
}
